import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  getDoc,
  doc,
} from 'firebase/firestore';
import { db } from '@root/firebase';
import TributeCard from '@components/TributeCard'; // <-- uses the reusable card
// If you still want inline toggles from the list, you can keep these:
// import LikeButton from '@components/LikeButton';
// import FavoriteButton from '@components/FavoriteButton';

const PAGE_LIMIT = 10;

export default function MemorialSearch() {
  const { globalUser } = useAuth();
  const uid = globalUser?.uid || null;

  const [searchTerm, setSearchTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [loading, setLoading] = useState(false);
  const [tributes, setTributes] = useState([]);

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Helper: fetch like/favorite booleans for one tribute
  const fetchLikeFavStatus = async (tributeId, userId) => {
    if (!userId) return { liked: false, favorited: false };
    // liked -> memorials/{id}/liked/{uid}
    const likeRef = doc(db, 'memorials', tributeId, 'liked', userId);
    const favRef = doc(db, 'memorials', tributeId, 'favorite', userId);
    const [likeSnap, favSnap] = await Promise.all([
      getDoc(likeRef),
      getDoc(favRef),
    ]);
    return {
      liked: likeSnap.exists(),
      favorited: favSnap.exists(),
    };
  };

  // Load latest public tributes on mount
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const memorialsRef = collection(db, 'memorials');
        const q = query(
          memorialsRef,
          where('privacy', '==', 'Public'),
          orderBy('createdDate', 'desc'),
          limit(PAGE_LIMIT)
        );
        const snap = await getDocs(q);

        const base = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // augment with like/fav for current user
        const augmented = await Promise.all(
          base.map(async (t) => {
            const { liked, favorited } = await fetchLikeFavStatus(t.id, uid);
            return { ...t, _liked: liked, _favorited: favorited };
          })
        );

        setTributes(augmented);
      } catch (e) {
        console.error('Error fetching recent tributes:', e);
        setTributes([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [uid]);

  // Search (client-side filtering of public, ordered by date)
  useEffect(() => {
    if (debounced === '') return; // keep recent list when the box is empty

    (async () => {
      setLoading(true);
      try {
        const memorialsRef = collection(db, 'memorials');
        const q = query(
          memorialsRef,
          where('privacy', '==', 'Public'),
          orderBy('createdDate', 'desc')
        );
        const snap = await getDocs(q);
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const term = debounced.toLowerCase();
        const filtered = all.filter((t) => {
          return (
            (t.mainTitle && t.mainTitle.toLowerCase().includes(term)) ||
            (t.subTitle && t.subTitle.toLowerCase().includes(term)) ||
            (t.message && t.message.toLowerCase().includes(term)) ||
            (t.dog && t.dog.toLowerCase().includes(term))
          );
        });

        const augmented = await Promise.all(
          filtered.map(async (t) => {
            const { liked, favorited } = await fetchLikeFavStatus(t.id, uid);
            return { ...t, _liked: liked, _favorited: favorited };
          })
        );

        setTributes(augmented);
      } catch (e) {
        console.error('Error searching tributes:', e);
        setTributes([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [debounced, uid]);

  return (
    <div className='col-lg-9'>
      {/* Search */}
      <div className='col-lg-12'>
        <div className='card'>
          <div className='card-body'>
            <form onSubmit={(e) => e.preventDefault()}>
              <div className='form-floating'>
                <input
                  type='text'
                  className='form-control'
                  id='txtSearch'
                  placeholder='Search for Memorials...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <label htmlFor='txtSearch'>Search for Memorials...</label>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <p className='mt-3'>Loading…</p>
      ) : tributes.length === 0 ? (
        <div className='card mt-3'>
          <div className='card-body'>
            <p className='mb-0 text-muted'>No memorials found.</p>
          </div>
        </div>
      ) : (
        <div className='row g-3 mt-3'>
          {tributes.map((t) => (
            <div key={t.id} className='col-xl-4 col-lg-4 col-md-6'>
              <TributeCard
                tribute={t}
                // If your TributeCard expects these for hiding owner dropdown:
                currentUid={uid}
                ownerUid={t.uid}
                showActions
                // If your TributeCard supports initial like/fav states:
                initialLiked={t._liked}
                initialFavorited={t._favorited}
                // If your TributeCard prop names differ, adjust here.
              />
              {/* If you aren't ready to use TributeCard, swap the block above with a simple card. */}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
